/**
 * Idempotency service: logica principale per operazioni idempotenti.
 */

import { telemetryService } from '../../services/offline/telemetry';
import { idempotencyStorage } from './idempotency-storage';
import { generateIdempotencyKey, hashPayload } from './key-generator';
import type { CreateIdempotencyKeyOptions } from './types';
import { debugLog } from '../../utils/debug';

export interface IdempotentOperationResult<T> {
  data: T;
  fromCache: boolean;
  idempotencyKey: string;
  serverId?: string;
}

export class IdempotencyService {
  async executeIdempotent<T>(
    options: CreateIdempotencyKeyOptions,
    operation: (idempotencyKey: string) => Promise<{ data: T; serverId?: string }>
  ): Promise<IdempotentOperationResult<T>> {
    const { operationType, localEntityId, payload, ttlHours } = options;
    const idempotencyKey = generateIdempotencyKey(operationType, localEntityId);
    const existingRecords = await idempotencyStorage.getByLocalEntityId(localEntityId);
    const currentHash = hashPayload(payload);

    const completedMatch = existingRecords.find(
      (r) => r.status === 'completed' && r.requestHash === currentHash
    );
    if (completedMatch?.cachedResponse) {
      debugLog(`[Idempotency] Cache hit for ${localEntityId}`);
      this._trackIdempotency('cache_hit', operationType, localEntityId);
      return {
        data: JSON.parse(completedMatch.cachedResponse) as T,
        fromCache: true,
        idempotencyKey: completedMatch.key,
        serverId: completedMatch.serverEntityId ?? undefined,
      };
    }

    const pendingMatch = existingRecords.find(
      (r) => r.status === 'pending' && r.requestHash === currentHash
    );
    if (pendingMatch) {
      debugLog(`[Idempotency] Recovering pending operation for ${localEntityId}`);
      return this._executeWithKey(
        pendingMatch.key,
        operationType,
        localEntityId,
        payload,
        operation
      );
    }

    return this._executeWithKey(
      idempotencyKey,
      operationType,
      localEntityId,
      payload,
      operation,
      ttlHours
    );
  }

  async isAlreadyCompleted(
    localEntityId: string,
    payload: unknown
  ): Promise<{
    completed: boolean;
    serverId?: string;
    cachedResponse?: unknown;
  }> {
    const records = await idempotencyStorage.getByLocalEntityId(localEntityId);
    const currentHash = hashPayload(payload);
    const match = records.find(
      (r) => r.status === 'completed' && r.requestHash === currentHash
    );
    if (match) {
      return {
        completed: true,
        serverId: match.serverEntityId ?? undefined,
        cachedResponse: match.cachedResponse ? JSON.parse(match.cachedResponse) : undefined,
      };
    }
    return { completed: false };
  }

  async getServerIdForLocal(localEntityId: string): Promise<string | null> {
    const records = await idempotencyStorage.getByLocalEntityId(localEntityId);
    const completed = records.find((r) => r.status === 'completed' && r.serverEntityId);
    return completed?.serverEntityId ?? null;
  }

  async cleanup(): Promise<void> {
    await idempotencyStorage.cleanup();
  }

  async getStats() {
    return idempotencyStorage.getStats();
  }

  private async _executeWithKey<T>(
    idempotencyKey: string,
    operationType: string,
    localEntityId: string,
    payload: unknown,
    operation: (key: string) => Promise<{ data: T; serverId?: string }>,
    ttlHours?: number
  ): Promise<IdempotentOperationResult<T>> {
    await idempotencyStorage.register(
      idempotencyKey,
      operationType,
      localEntityId,
      payload,
      ttlHours
    );

    try {
      const result = await operation(idempotencyKey);
      await idempotencyStorage.markCompleted(
        idempotencyKey,
        result.serverId ?? localEntityId,
        result.data,
        201
      );
      this._trackIdempotency('completed', operationType, localEntityId, result.serverId);
      return {
        data: result.data,
        fromCache: false,
        idempotencyKey,
        serverId: result.serverId,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      await idempotencyStorage.markFailed(idempotencyKey, errorMessage);
      this._trackIdempotency('failed', operationType, localEntityId, undefined, errorMessage);
      throw error;
    }
  }

  private _trackIdempotency(
    action: string,
    operationType: string,
    localEntityId: string,
    serverId?: string,
    error?: string
  ): void {
    telemetryService.track({
      type: 'idempotency',
      data: { action, operationType, localEntityId, serverId, error },
    });
  }
}

export const idempotencyService = new IdempotencyService();
